import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient } from '@wix/sdk-types';
import {
  EventsViewExtendedEnvelope,
  EventsViewProjectionUpdatedEnvelope,
  GetEventsViewResponse,
  getEventsView as universalGetEventsView,
} from './calendar-v3-events-view-event-views.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/calendar' };

export function getEventsView(httpClient: HttpClient): GetEventsViewSignature {
  return () =>
    universalGetEventsView(
      // @ts-ignore
      { httpClient }
    );
}

interface GetEventsViewSignature {
  /**
   * Doesn’t return details about events within the view. You can use
   * [queryEvents()](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/query-events),
   * providing `eventsView.endDate` as `toLocalDate` to retrieve the
   * events.
   */
  (): Promise<GetEventsViewResponse>;
}

export const onEventsViewExtended = EventDefinition(
  'wix.calendar.v3.events_view_extended',
  true,
  (event: EventsViewExtendedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.extendedEndDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<EventsViewExtendedEnvelope>();
export const onEventsViewProjectionUpdated = EventDefinition(
  'wix.calendar.v3.events_view_projection_updated',
  true,
  (event: EventsViewProjectionUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.eventAddedOrUpdated.event.createdDate' },
            { path: 'data.eventAddedOrUpdated.event.updatedDate' },
            { path: 'data.eventAddedOrUpdated.event.start.utcDate' },
            { path: 'data.eventAddedOrUpdated.event.end.utcDate' },
            { path: 'data.eventAddedOrUpdated.event.adjustedStart.utcDate' },
            { path: 'data.eventAddedOrUpdated.event.adjustedEnd.utcDate' },
            {
              path: 'data.eventAddedOrUpdated.event.recurrenceRule.until.utcDate',
            },
            {
              path: 'data.eventAddedOrUpdated.event.recurrenceRule.adjustedUntil.utcDate',
            },
            { path: 'data.eventAddedOrUpdated.previousEvent.createdDate' },
            { path: 'data.eventAddedOrUpdated.previousEvent.updatedDate' },
            { path: 'data.eventAddedOrUpdated.previousEvent.start.utcDate' },
            { path: 'data.eventAddedOrUpdated.previousEvent.end.utcDate' },
            {
              path: 'data.eventAddedOrUpdated.previousEvent.adjustedStart.utcDate',
            },
            {
              path: 'data.eventAddedOrUpdated.previousEvent.adjustedEnd.utcDate',
            },
            {
              path: 'data.eventAddedOrUpdated.previousEvent.recurrenceRule.until.utcDate',
            },
            {
              path: 'data.eventAddedOrUpdated.previousEvent.recurrenceRule.adjustedUntil.utcDate',
            },
            { path: 'data.eventRemoved.event.createdDate' },
            { path: 'data.eventRemoved.event.updatedDate' },
            { path: 'data.eventRemoved.event.start.utcDate' },
            { path: 'data.eventRemoved.event.end.utcDate' },
            { path: 'data.eventRemoved.event.adjustedStart.utcDate' },
            { path: 'data.eventRemoved.event.adjustedEnd.utcDate' },
            { path: 'data.eventRemoved.event.recurrenceRule.until.utcDate' },
            {
              path: 'data.eventRemoved.event.recurrenceRule.adjustedUntil.utcDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<EventsViewProjectionUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  CommonIdentificationData,
  CommonIdentificationDataIdOneOf,
  ConferencingDetails,
  Day,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Event,
  EventAddedOrUpdated,
  EventMetadata,
  EventRemoved,
  EventsView,
  EventsViewExtended,
  EventsViewExtendedEnvelope,
  EventsViewProjectionUpdated,
  EventsViewProjectionUpdatedEnvelope,
  EventsViewProjectionUpdatedTypeOneOf,
  ExtendEventsViewRequest,
  ExtendEventsViewResponse,
  ExtendedFields,
  Field,
  Frequency,
  GetEventsViewRequest,
  GetEventsViewResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  IdentityType,
  Location,
  LocationType,
  MessageEnvelope,
  OnlineMeetingInfo,
  Participant,
  Participants,
  ParticipantsStatus,
  Permission,
  Provider,
  ProviderProviderInfoOneOf,
  ProviderType,
  RecurrenceRule,
  RecurrenceType,
  Resource,
  RestoreInfo,
  Role,
  Status,
  Transparency,
  Type,
  WebhookIdentityType,
  ZonedDate,
} from './calendar-v3-events-view-event-views.universal.js';
